import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const lucenceCardioHemeRiskQuestions = [
  {
    label: 'What was your sex assigned at birth?',
    field: 'lucenceCardioHemeRisk_sexAssignedAtBirth',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'male', value: 'Male' },
        { key: 'female', value: 'Female' }
      ]
    }
  },
  {
    label: 'Have you ever had an allogeneic bone marrow transplant?',
    field: 'lucenceCardioHemeRisk_boneMarrowTransplant',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Have you had a blood transfusion in the past 14 days?',
    field: 'lucenceCardioHemeRisk_bloodTransfusion14Days',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Past history of cancer?',
    field: 'lucenceCardioHemeRisk_cancerHistory',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label:
      'Please provide information about the type of cancer and the year of the diagnosis',
    field: 'lucenceCardioHemeRisk_cancerDetails',
    type: 'info',
    answer: {
      type: 'text',
      name: 'lucenceCardioHemeRisk_cancerDetails'
    }
  },
  {
    // Only asked by the `lucenceInsight` flow; CardioHemeRISK skips it.
    label:
      'Pick the top three cancers that you either have family history of or are concerned about (lung, colorectal, breast, pancreatic, thyroid, and liver are already included). Your physician will review these before ordering the test.',
    field: 'lucenceCardioHemeRisk_cancerSelection',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'stomach', value: 'Stomach' },
        { key: 'bladder', value: 'Bladder' },
        { key: 'prostate', value: 'Prostate' },
        { key: 'ovarian', value: 'Ovarian' },
        { key: 'endometrial', value: 'Endometrial' },
        { key: 'cervical', value: 'Cervical' },
        { key: 'lymphoma', value: 'Lymphoma (Diffuse large B-cell lymphoma)' },
        { key: 'nasopharyngeal', value: 'Nasopharyngeal' },
        { key: 'oropharyngeal', value: 'Oropharyngeal (HPV-positive)' },
        { key: 'esophageal', value: 'Esophageal' },
        { key: 'biliaryTract', value: 'Biliary tract' },
        { key: 'skinMelanoma', value: 'Skin (melanoma)' }
      ]
    }
  },
  {
    label: 'What other information or questions do you have for the doctor?',
    field: 'lucenceCardioHemeRisk_otherInfoForDoctor',
    type: 'info',
    answer: {
      type: 'text',
      name: 'lucenceCardioHemeRisk_otherInfoForDoctor'
    }
  },
  {
    label: 'Consent (Cardio Heme Risk Testing)',
    field: 'lucenceCardioHemeRisk_consent',
    type: 'select',
    educationalSlide: `This testing is intended to evaluate markers associated with cardiovascular risk, including inflammation, lipid-related markers, and other risk factors. It is designed to provide insight into your overall heart health and identify potential areas for further evaluation or risk reduction.

By proceeding, you acknowledge and understand the following:

Purpose of Testing: This test is for risk assessment and general health insight. It is not intended to diagnose, treat, or monitor a specific medical condition. This encounter is not appropriate if you are experiencing active symptoms such as chest pain, shortness of breath, or other concerning symptoms.

Scope of Encounter: This is not a substitute for in-person medical care. No physical exam or comprehensive diagnostic evaluation is performed. Results should be reviewed with your primary care provider or a specialist, especially if abnormal.

Provider Feedback: You will receive general feedback on your results, which may include educational information, lifestyle considerations, and recommendations for follow-up care if needed.

Limitations: Assessment is based only on your lab results and the information you provide. It does not include a full medical history review or diagnostic workup.

Follow-Up Responsibility: You are responsible for seeking appropriate medical care for any abnormal findings, symptoms, or ongoing health concerns.

Truthfulness: I verify that I am the patient completing this form. I confirm that all information I have provided is true, accurate, and complete to the best of my knowledge. I understand that incomplete or inaccurate information may impact the safety and accuracy of my assessment, and I will not hold the provider or affiliated medical practice responsible for any omissions or inaccuracies in the information I provided.

LUCENCE TEST CONSENT AND AUTHORIZATION

I understand that Lucence Health Inc.'s ("Lucence") CardioHemeRISK™ and LucenceINSIGHT™ (each, the "Test") analyzes my genetic profile based on the blood samples that I provide ("Sample Material"). By signing this form, you authorize the collection of your Sample Material and its transfer to Lucence and where applicable for LucenceINSIGHT™, to Lucence's CAP-accredited and CLIA-licensed reference laboratory in Singapore, for performing the Test(s) and for the purposes set out below.

I understand that the turnaround time given for the Test(s) is an indicative guide only. As the performance of the Test(s) may require the input of third parties and involve factors not within Lucence's control, I understand that Lucence is unable to guarantee turnaround time. However, Lucence shall keep my physician informed of any unusual delays in providing the Test results and my physician shall have the duty to communicate such information to me. I shall not hold Lucence liable for any loss of profits, indirect, consequential or special damages or losses I may suffer or incur in connection with the Test(s), including but not limited to delays in delivery of the Test(s) results or diagnostic information provided to me by my physician in reliance on the results of the Test(s).

USE OF PERSONAL INFORMATION, AND USE OF SAMPLE MATERIAL FOR THE PURPOSES OF RUNNING THE ORDERED TEST OR FOR QUALITY TESTING

"Personal Information" refers to information that can be used to identify a natural person. The personal information Lucence may from time to time collect from you include your name, nationality, date of birth, sex, e-mail address, telephone number, mailing address, photographic image, medical history, allergy information, genetic test results, and other health records.

You agree that Lucence may collect, use, disclose, process and transfer your Personal Information for the following purposes, but always in accordance with applicable laws and regulations:

- providing you with healthcare, diagnostic and other services of Lucence, its affiliates, partners and related companies and for its company processes;
- administrative functions, (e.g., processing requisitions; collecting payment; creation and maintenance of medical and business records; verifying identity and conducting screenings, due diligence and credit checks; responding to your queries; addressing claims or disputes; compliance with internal policies; and enforcing obligations to Lucence);
- business operations (e.g., compliance with regulatory obligations, accounting, audit and record keeping, planning, product monitoring/assessment quality control, training, product testing / development); and/or
- research into new treatments and protocols (subject always to applicable laws and codes of conduct).

The results of your Test(s), including your genetic data, will form part of your confidential medical records. These results will be accessible to your treating physician and his/her hospital or clinic, in addition to Lucence.

Your Test(s) results and clinical data may be added to and retained in databases for a reasonable period in accordance with Lucence's legal and business purposes, and subject to applicable laws and regulations.

Your Sample Material may be examined at the time of the Test(s) or thereafter, possibly using new methods or technologies for the purposes of running the ordered Test(s) or for quality testing. Through your signature, you consent to such use of your Sample Material and information.

You may, at any time, correct, have access to, and/or revoke your authorization to any of the above uses of your Personal Information (except to the extent Lucence has already acted in reliance on the authorization). Contact us at privacy@lucence.com or visit www.lucence.com/legal for more details on Lucence's privacy policy.

The services provided by Lucence are subject to further terms and conditions which are found on Lucence's website (www.lucence.com/legal), all of which are incorporated herein by reference.

By selecting below, you confirm that you have read and understand the above information and consent to proceed with Cardio Heme Risk testing and review.`,
    answer: {
      type: 'single_select',
      options: [
        {
          key: 'non_consent',
          class: 'option-decline',
          value:
            'I have read the above information and I do not wish to continue'
        },
        {
          key: 'consent',
          class: 'option-consent',
          value:
            'I have read the above information and I do consent and wish to move forward'
        }
      ]
    }
  },
  {
    label: 'Consent to Contact by Lucence (optional)',
    field: 'lucenceCardioHemeRisk_lucenceContactConsent',
    type: 'select',
    educationalSlide: `By checking Yes to any of the box(es) below and providing my signature, I consent to receive phone calls and/or texts from or on behalf of Lucence about the Lucence Financial Assistance Program (the "Program") and/or participation in Lucence's future research studies at the phone number provided above. Consent to such communication is not required for participation in the Program, and does not obligate you to participate in the Program or in future research studies.

I understand that Lucence may contact me by email or mail at the address(es) provided above about the Program, as part of my eligibility determination or continued enrollment eligibility, future research studies, and for any other reasons as described here and in our Privacy Policy (available at www.lucence.com/legal).

Select all that apply. Declining does not affect your testing or this visit.`,
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'financialAssistanceProgram',
          value:
            'Yes, I consent to receive phone calls and/or texts about the Lucence Financial Assistance Program'
        },
        {
          key: 'futureResearchStudies',
          value:
            "Yes, I consent to receive phone calls and/or texts about participation in Lucence's future research studies"
        },
        {
          key: 'noContactConsent',
          value: 'No, I do not consent to either of the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'lucenceCardioHemeRisk_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'lucenceCardioHemeRisk_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    type: 'info',
    isLast: true,
    blockQuestionFlow: true,
    answer: {},
    extras: { sendToBeluga: false }
  }
] as const;

export type LucenceCardioHemeRISKQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof lucenceCardioHemeRiskQuestions>
>;
export default lucenceCardioHemeRiskQuestions;
